import React from "react";
import {
  Box,
  Card,
  CardActionArea,
  Typography,
  Stack,
  IconButton,
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  Badge,
} from "@mui/material";
import {
  ZoomIn as ZoomInIcon,
  Close as CloseIcon,
  NavigateBefore as PrevIcon,
  NavigateNext as NextIcon,
} from "@mui/icons-material";
import ProgressPhotosPrivacyGate from "./molecules/ProgressPhotosPrivacyGate";

// Enhanced PhotoThumbnails component: displays nice thumbnails for an array of photo URLs with click-to-expand
function PhotoThumbnails({ photoURLs, requireReveal = true }) {
  const [selectedPhotoIndex, setSelectedPhotoIndex] = React.useState(null);
  const [openPhotoDialog, setOpenPhotoDialog] = React.useState(false);

  if (!Array.isArray(photoURLs) || photoURLs.length === 0) return null;

  // Filter out any falsy or non-string URLs
  const validURLs = photoURLs.filter(
    (url) => typeof url === "string" && url.trim() !== "",
  );
  if (validURLs.length === 0) return null;

  const resetKey = validURLs.join("|");

  const handlePhotoClick = (index) => {
    setSelectedPhotoIndex(index);
    setOpenPhotoDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenPhotoDialog(false);
    setSelectedPhotoIndex(null);
  };

  const handlePrevPhoto = () => {
    if (selectedPhotoIndex > 0) {
      setSelectedPhotoIndex(selectedPhotoIndex - 1);
    }
  };

  const handleNextPhoto = () => {
    if (selectedPhotoIndex < validURLs.length - 1) {
      setSelectedPhotoIndex(selectedPhotoIndex + 1);
    }
  };

  const thumbnails = (
    <>
      <Box sx={{ mt: requireReveal ? 0 : 2 }}>
        <Typography
          variant="body2"
          fontWeight="medium"
          sx={{ mb: 1, color: "text.secondary" }}
        >
          Progress Photos ({validURLs.length})
        </Typography>
        <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1 }}>
          {validURLs.map((url, idx) => (
            <Card
              key={idx}
              sx={{
                borderRadius: 1.5,
                overflow: "hidden",
                boxShadow: 2,
                cursor: "pointer",
                transition: "all 0.3s ease",
                "&:hover": {
                  transform: "scale(1.05)",
                  boxShadow: 4,
                },
                position: "relative",
              }}
              onClick={() => handlePhotoClick(idx)}
            >
              <CardActionArea>
                <Box sx={{ position: "relative" }}>
                  <Box
                    component="img"
                    src={url}
                    alt={`Progress photo ${idx + 1}`}
                    sx={{
                      width: 80,
                      height: 80,
                      objectFit: "cover",
                      display: "block",
                    }}
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: "rgba(0,0,0,0.4)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      opacity: 0,
                      transition: "opacity 0.3s ease",
                      "&:hover": {
                        opacity: 1,
                      },
                    }}
                  >
                    <ZoomInIcon sx={{ color: "white", fontSize: 24 }} />
                  </Box>
                </Box>
              </CardActionArea>
              <Badge
                badgeContent={idx + 1}
                color="primary"
                sx={{
                  position: "absolute",
                  top: 12,
                  right: 12,
                  "& .MuiBadge-badge": {
                    fontSize: "0.6rem",
                    minWidth: 16,
                    height: 16,
                  },
                }}
              />
            </Card>
          ))}
        </Stack>
      </Box>

      {/* Photo Viewer Dialog */}
      <Dialog
        open={openPhotoDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: "grey.900",
            color: "white",
          },
        }}
      >
        <DialogContent sx={{ p: 1, position: "relative" }}>
          <IconButton
            onClick={handleCloseDialog}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              color: "white",
              bgcolor: "rgba(0,0,0,0.5)",
              zIndex: 2,
              "&:hover": {
                bgcolor: "rgba(0,0,0,0.7)",
              },
            }}
          >
            <CloseIcon />
          </IconButton>

          {selectedPhotoIndex !== null && (
            <Box sx={{ textAlign: "center" }}>
              <img
                src={validURLs[selectedPhotoIndex]}
                alt={`Progress photo ${selectedPhotoIndex + 1}`}
                style={{
                  maxWidth: "100%",
                  maxHeight: "70vh",
                  objectFit: "contain",
                  borderRadius: 8,
                }}
              />
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ justifyContent: "space-between", px: 2, py: 1 }}>
          <Button
            onClick={handlePrevPhoto}
            disabled={selectedPhotoIndex === 0}
            startIcon={<PrevIcon />}
            sx={{ color: "white" }}
          >
            Previous
          </Button>

          <Typography variant="body2" sx={{ color: "white" }}>
            {selectedPhotoIndex !== null ? selectedPhotoIndex + 1 : 0} of{" "}
            {validURLs.length}
          </Typography>

          <Button
            onClick={handleNextPhoto}
            disabled={selectedPhotoIndex === validURLs.length - 1}
            endIcon={<NextIcon />}
            sx={{ color: "white" }}
          >
            Next
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );

  if (!requireReveal) {
    return <Box sx={{ mt: 2 }}>{thumbnails}</Box>;
  }

  return (
    <Box sx={{ mt: 2 }}>
      <ProgressPhotosPrivacyGate
        resetKey={resetKey}
        title="Progress photos are hidden"
        description="Reveal only when you intend to review them — helpful if someone is nearby."
        revealLabel="Reveal photos"
      >
        {thumbnails}
      </ProgressPhotosPrivacyGate>
    </Box>
  );
}

export default PhotoThumbnails;
